import { useRef, useCallback, useState } from "react";

/**
 * Secret unlock pattern: tap-tap-tap-and-hold(3s).
 *  - tap 1 → counts up
 *  - tap 2 → counts up
 *  - 3rd press → must be held HOLD_MS to trigger
 *  - tap window resets after RESET_MS of inactivity
 *
 * Returns props you can spread onto an element + visual state for feedback.
 */
const RESET_MS = 1500;     // gap allowed between taps
const HOLD_MS = 3000;      // hold duration on 3rd press

export default function useSecretUnlock(onUnlock) {
  const countRef = useRef(0);
  const lastTapRef = useRef(0);
  const holdTimerRef = useRef(null);
  const [holdProgress, setHoldProgress] = useState(0); // 0..100
  const [isHolding, setIsHolding] = useState(false);
  const progressIntervalRef = useRef(null);

  const reset = useCallback(() => {
    countRef.current = 0;
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
    if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
    setHoldProgress(0);
    setIsHolding(false);
  }, []);

  const onPointerDown = useCallback((e) => {
    e.preventDefault?.();
    const now = Date.now();
    // reset count if too much time has passed
    if (now - lastTapRef.current > RESET_MS) {
      countRef.current = 0;
    }

    // If this is the 3rd press → start hold timer
    if (countRef.current === 2) {
      setIsHolding(true);
      const start = Date.now();
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - start;
        setHoldProgress(Math.min(100, (elapsed / HOLD_MS) * 100));
      }, 50);
      holdTimerRef.current = setTimeout(() => {
        // Unlocked!
        reset();
        try { onUnlock?.(); } catch (e) { /* ignore */ }
      }, HOLD_MS);
    }
  }, [onUnlock, reset]);

  const onPointerUp = useCallback(() => {
    const now = Date.now();

    // If the 3rd-press hold timer is running, the user released early → reset
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
      setHoldProgress(0);
      setIsHolding(false);
      countRef.current = 0; // released too early — start over
      lastTapRef.current = 0;
      return;
    }

    // Normal tap completion
    countRef.current += 1;
    lastTapRef.current = now;
    if (countRef.current >= 3) countRef.current = 0;
  }, []);

  const onPointerLeave = useCallback(() => {
    // If they drag off the element, cancel any in-progress hold
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
      setHoldProgress(0);
      setIsHolding(false);
      countRef.current = 0;
    }
  }, []);

  return {
    bind: {
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      onPointerCancel: onPointerLeave,
    },
    holdProgress,
    isHolding,
  };
}
